import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Form, Spinner } from 'react-bootstrap';
import Icon from '../../common/Icons';
import ClinicalButton from '../../doctor/ui/ClinicalButton';
import consultationService from '../../../services/consultationService';
import nursingTestCatalogService from '../../../services/nursingTestCatalogService';
import { NURSE_COPY } from '../nurseCopy';

const C = NURSE_COPY.TEST_CHECKLIST;

function buildRecordedTestsMap(testResults) {
  const map = new Map();
  (testResults || []).forEach((t) => {
    if (t.catalog_item_id != null && String(t.catalog_item_id).trim() !== '') {
      const key = String(t.catalog_item_id);
      if (!map.has(key)) {
        map.set(key, {
          result_value: t.result_value || '',
          result_unit: t.result_unit || '',
          notes: t.notes || ''
        });
      }
    }
  });
  return map;
}

function buildRecordedCatalogIds(map) {
  return new Set(map.keys());
}

function StatPill({ label, value, tone = 'default' }) {
  return (
    <div className={`nr-care-stat nr-care-stat--${tone}`}>
      <span className="nr-care-stat__value">{value}</span>
      <span className="nr-care-stat__label">{label}</span>
    </div>
  );
}

export default function NurseTestChecklist({
  consultationId,
  toast,
  timeline,
  onTimelineRefresh
}) {
  const [catalog, setCatalog] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [resultDrafts, setResultDrafts] = useState(() => new Map());
  const [globalNote, setGlobalNote] = useState('');
  const [viewFilter, setViewFilter] = useState('ALL');

  const recordedMap = useMemo(
    () => buildRecordedTestsMap(timeline?.testResults),
    [timeline?.testResults]
  );

  const recordedIds = useMemo(() => buildRecordedCatalogIds(recordedMap), [recordedMap]);

  const manualTests = useMemo(
    () => (timeline?.testResults || []).filter((t) => !t.catalog_item_id),
    [timeline?.testResults]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingCatalog(true);
      try {
        const res = await nursingTestCatalogService.list({ is_active: 'true' });
        if (!cancelled) {
          setCatalog(res.success && Array.isArray(res.data) ? res.data : []);
        }
      } catch {
        if (!cancelled) setCatalog([]);
      } finally {
        if (!cancelled) setLoadingCatalog(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const syncKey = useMemo(
    () =>
      [...recordedIds].sort().join(',') +
      '|' +
      [...recordedMap.entries()].map(([k, v]) => `${k}:${v.result_value}`).join(';'),
    [recordedIds, recordedMap]
  );

  useEffect(() => {
    const map = buildRecordedTestsMap(timeline?.testResults);
    setSelectedIds(new Set(map.keys()));
    setResultDrafts(new Map());
  }, [syncKey, timeline?.testResults]);

  const sortedCatalog = useMemo(
    () =>
      [...catalog].sort(
        (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.name.localeCompare(b.name, 'fr')
      ),
    [catalog]
  );

  const pendingAddIds = useMemo(
    () => [...selectedIds].filter((id) => !recordedIds.has(id)),
    [selectedIds, recordedIds]
  );

  const visibleItems = useMemo(() => {
    if (viewFilter === 'CHECKED') {
      return sortedCatalog.filter((item) => selectedIds.has(String(item.id)));
    }
    if (viewFilter === 'PENDING') {
      return sortedCatalog.filter(
        (item) => selectedIds.has(String(item.id)) && !recordedIds.has(String(item.id))
      );
    }
    return sortedCatalog;
  }, [sortedCatalog, viewFilter, selectedIds, recordedIds]);

  const setResultValue = useCallback((catalogId, value) => {
    const id = String(catalogId);
    setResultDrafts((prev) => {
      const next = new Map(prev);
      const cur = next.get(id) || { result_value: '', notes: '' };
      next.set(id, { ...cur, result_value: value });
      return next;
    });
  }, []);

  const toggleItem = useCallback(
    (catalogId) => {
      const id = String(catalogId);
      if (recordedIds.has(id)) return;

      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
          setResultDrafts((drafts) => {
            const d = new Map(drafts);
            d.delete(id);
            return d;
          });
        } else {
          next.add(id);
          setResultDrafts((drafts) => {
            const d = new Map(drafts);
            if (!d.has(id)) d.set(id, { result_value: '', notes: '' });
            return d;
          });
        }
        return next;
      });
    },
    [recordedIds]
  );

  const selectAllVisible = useCallback(() => {
    const ids = visibleItems.map((item) => String(item.id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => {
        if (!recordedIds.has(id)) next.add(id);
      });
      return next;
    });
    setResultDrafts((drafts) => {
      const d = new Map(drafts);
      ids.forEach((id) => {
        if (!recordedIds.has(id) && !d.has(id)) d.set(id, { result_value: '', notes: '' });
      });
      return d;
    });
  }, [visibleItems, recordedIds]);

  const clearNewSelection = useCallback(() => {
    setSelectedIds(new Set(recordedIds));
    setResultDrafts(new Map());
  }, [recordedIds]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!pendingAddIds.length) {
      toast('warning', C.NOTHING_NEW);
      return;
    }

    const missing = pendingAddIds.filter((id) => !String(resultDrafts.get(id)?.result_value || '').trim());
    if (missing.length) {
      toast('warning', C.MISSING_RESULTS.replace('{n}', String(missing.length)));
      return;
    }

    setBusy(true);
    try {
      const sharedNote = globalNote.trim() || null;
      for (const idStr of pendingAddIds) {
        const item = catalog.find((c) => String(c.id) === idStr);
        if (!item) continue;
        const draft = resultDrafts.get(idStr) || {};
        const res = await consultationService.addNursingTestResult(consultationId, {
          catalog_item_id: parseInt(idStr, 10),
          test_name: item.name,
          result_value: String(draft.result_value).trim(),
          result_unit: item.result_unit || null,
          notes: draft.notes?.trim() || sharedNote
        });
        if (!res.success) {
          throw new Error(res.message || `Enregistrement impossible pour ${item.name}`);
        }
      }
      toast(
        'success',
        pendingAddIds.length === 1
          ? C.SAVED_ONE
          : C.SAVED_MANY.replace('{n}', String(pendingAddIds.length))
      );
      setGlobalNote('');
      await onTimelineRefresh?.();
    } catch (err) {
      toast('danger', err.message || C.SAVE_ERROR);
    } finally {
      setBusy(false);
    }
  };

  if (loadingCatalog) {
    return (
      <div className="nr-care-board nr-care-board--loading">
        <Spinner animation="border" size="sm" variant="success" />
        <p className="text-muted small mt-2 mb-0">{C.LOADING}</p>
      </div>
    );
  }

  if (!catalog.length) {
    return (
      <Alert variant="warning" className="mb-0 border-0">
        {C.EMPTY_CATALOG}
      </Alert>
    );
  }

  return (
    <Form onSubmit={handleSave} className="nr-care-board">
      <header className="nr-care-board__header">
        <p className="nr-care-board__intro">{C.INTRO}</p>
        <div className="nr-care-board__stats">
          <StatPill label={C.STAT_TOTAL} value={catalog.length} />
          <StatPill label={C.STAT_CHECKED} value={selectedIds.size} tone="active" />
          <StatPill label={C.STAT_SAVED} value={recordedIds.size} tone="saved" />
          {pendingAddIds.length > 0 ? (
            <StatPill label={C.STAT_PENDING} value={pendingAddIds.length} tone="pending" />
          ) : null}
        </div>

        <div className="nr-care-board__filters">
          <div className="nr-care-board__filter-row" role="group" aria-label={C.FILTER_VIEW_ARIA}>
            {[
              { id: 'ALL', label: C.FILTER_ALL },
              { id: 'CHECKED', label: C.FILTER_CHECKED },
              { id: 'PENDING', label: C.FILTER_PENDING }
            ].map((f) => (
              <button
                key={f.id}
                type="button"
                className={`nr-care-chip${viewFilter === f.id ? ' is-active' : ''}`}
                onClick={() => setViewFilter(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="nr-care-board__quick">
          <button type="button" className="nr-care-link-btn" onClick={selectAllVisible} disabled={busy}>
            {C.SELECT_ALL_VISIBLE}
          </button>
          <span className="nr-care-board__quick-sep" aria-hidden>
            ·
          </span>
          <button type="button" className="nr-care-link-btn" onClick={clearNewSelection} disabled={busy}>
            {C.CLEAR_NEW}
          </button>
        </div>
      </header>

      <div className="nr-care-board__scroll">
        {visibleItems.length === 0 ? (
          <p className="nr-care-board__empty text-muted small">{C.EMPTY_FILTER}</p>
        ) : (
          <div className="nr-care-grid">
            {visibleItems.map((item) => {
              const idStr = String(item.id);
              const isSelected = selectedIds.has(idStr);
              const isRecorded = recordedIds.has(idStr);
              const isPending = isSelected && !isRecorded;
              const recorded = recordedMap.get(idStr);
              const draft = resultDrafts.get(idStr);
              const unitHint = item.result_unit ? ` (${item.result_unit})` : '';

              return (
                <div
                  key={item.id}
                  className={[
                    'nr-care-card',
                    'nr-test-card',
                    isSelected ? 'is-checked' : '',
                    isRecorded ? 'is-recorded' : '',
                    isPending ? 'is-pending' : ''
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <button
                    type="button"
                    className="nr-test-card__head"
                    onClick={() => toggleItem(item.id)}
                    disabled={busy || isRecorded}
                    aria-pressed={isSelected}
                  >
                    <span className="nr-care-card__mark" aria-hidden>
                      {isSelected ? (
                        <Icon name="checkCircle" size={18} />
                      ) : (
                        <span className="nr-care-card__box" />
                      )}
                    </span>
                    <span className="nr-care-card__body">
                      <span className="nr-care-card__name">{item.name}</span>
                      {item.description ? (
                        <span className="nr-care-card__desc">{item.description}</span>
                      ) : null}
                      {item.result_unit ? (
                        <span className="nr-test-card__unit">{C.UNIT_LABEL}: {item.result_unit}</span>
                      ) : null}
                    </span>
                    {isRecorded ? (
                      <span className="nr-care-card__status">{C.RECORDED_BADGE}</span>
                    ) : isPending ? (
                      <span className="nr-care-card__status nr-care-card__status--pending">
                        {C.PENDING_BADGE}
                      </span>
                    ) : null}
                  </button>

                  {isRecorded && recorded ? (
                    <div className="nr-test-card__result-display" aria-label={C.RESULT_LABEL}>
                      <strong>{recorded.result_value}</strong>
                      {recorded.result_unit ? (
                        <span className="text-muted ms-1">{recorded.result_unit}</span>
                      ) : null}
                    </div>
                  ) : null}

                  {isPending ? (
                    <div className="nr-test-card__fields">
                      <Form.Label className="nr-test-card__field-label">{C.RESULT_LABEL}</Form.Label>
                      <Form.Control
                        size="sm"
                        className="nr-test-card__result-input"
                        value={draft?.result_value || ''}
                        onChange={(e) => setResultValue(item.id, e.target.value)}
                        placeholder={item.result_unit || C.RESULT_PLACEHOLDER}
                        disabled={busy}
                        required
                        aria-required="true"
                      />
                      {unitHint ? (
                        <Form.Text className="nr-test-card__hint">{C.RESULT_HINT}{unitHint}</Form.Text>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}

        {manualTests.length > 0 ? (
          <aside className="nr-care-manual">
            <span className="nr-care-manual__title">{C.MANUAL_TITLE}</span>
            <ul className="nr-care-manual__list">
              {manualTests.map((t) => (
                <li key={t.id}>
                  {t.catalog_name || t.test_name}: <strong>{t.result_value}</strong>
                  {t.result_unit ? ` ${t.result_unit}` : ''}
                </li>
              ))}
            </ul>
          </aside>
        ) : null}
      </div>

      <footer className="nr-care-board__footer">
        <Form.Control
          className="nr-care-board__note"
          value={globalNote}
          onChange={(e) => setGlobalNote(e.target.value)}
          placeholder={C.GLOBAL_NOTE_PLACEHOLDER}
          disabled={busy}
          aria-label={C.GLOBAL_NOTE_LABEL}
        />
        <ClinicalButton
          variant="save"
          type="submit"
          loading={busy}
          icon="checkCircle"
          className="nr-care-board__save"
          disabled={!pendingAddIds.length && !busy}
        >
          {pendingAddIds.length > 0
            ? C.SAVE_BUTTON.replace('{n}', String(pendingAddIds.length))
            : C.SAVE_BUTTON_NONE}
        </ClinicalButton>
      </footer>
    </Form>
  );
}
